import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, RefreshCw, Search } from 'lucide-react';

import { useShopPath } from '@/hooks/useShopPath';

/**
 * Secondary status strip rendered BELOW the storefront card in the centre column
 * of "Your first 30 days" (ported from the fresh-dashboard `CenterStatusCard`).
 * Compact by design — the storefront card is the hero, this is the supporting
 * Google-listings status. Content follows the 4-status matrix:
 *
 *   none        → no strip (the storefront card fills the column)
 *   storefront  → soft "Google approval in progress" pending strip (inline spinner)
 *   listings    → soft "live on Google" celebration (no CTA, Google-only)
 *   both        → brand-gradient "live on Google" celebration with a results CTA
 *
 * When `googleDisapproved` is set the celebration is replaced by a warning — Google
 * has the channel but is rejecting everything (NEA-5878; not in the Lovable mock).
 *
 * @param {{ status: 'none' | 'listings' | 'storefront' | 'both', googleDisapproved?: boolean }} props
 */
export function CenterStatusStrip({ status, googleDisapproved }) {
	const { shopPath } = useShopPath();

	if (status === 'none') return null;

	const cardBase = 'relative shrink-0 overflow-hidden rounded-2xl';
	const softShadow = 'shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

	if (googleDisapproved) {
		return (
			<div className={`${cardBase} border border-warning/30 bg-warning-soft/40 p-4 ${softShadow} md:p-5`}>
				<h3 className="flex items-center gap-2 text-base font-bold leading-tight text-foreground md:text-[17px]">
					<AlertTriangle className="h-5 w-5 shrink-0 text-warning-soft-foreground" />
					Google is rejecting your products
				</h3>
				<p className="mt-1.5 text-sm text-warning-soft-foreground">
					All submitted products are disapproved — none are showing on Google.
				</p>
				<Link
					to={shopPath('/channels/google/manage')}
					className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-action hover:underline"
				>
					View details <ChevronRight className="h-4 w-4" />
				</Link>
			</div>
		);
	}

	if (status === 'storefront') {
		// Pending — Google has the products but they're still in approval.
		return (
			<div className={`${cardBase} border border-border/60 bg-gradient-soft p-4 ${softShadow} md:p-5`}>
				<h3 className="flex items-center gap-2 text-base font-bold leading-tight text-foreground md:text-[17px]">
					<RefreshCw className="h-5 w-5 shrink-0 animate-spin text-action" style={{ animationDuration: '3s' }} />
					Google Local Listings
				</h3>
				<p className="mt-1.5 text-sm text-muted-foreground">
					Your in-store products are with Google for approval. This can take up to 10 days.
				</p>
			</div>
		);
	}

	// listings | both — live-on-Google celebration. The brand-gradient + results
	// CTA are reserved for `both`; the Google-only `listings` strip stays soft.
	// In the `both` split the card is a flex column with the content growing, so the
	// CTA pins to the card bottom at the same p-4 offset as the storefront card's
	// button above it (deliberate deviation from the design's centred-flow layout).
	const isBoth = status === 'both';
	return (
		<div
			className={
				isBoth
					? `${cardBase} flex flex-col bg-gradient-unify p-4 text-white shadow-[0_12px_32px_-16px_hsl(var(--primary)/0.55)]`
					: `${cardBase} border border-border/60 bg-gradient-soft p-3 text-foreground ${softShadow} md:p-3.5`
			}
		>
			<div
				className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full blur-2xl ${isBoth ? 'bg-white/15' : 'bg-primary/10'}`}
			/>
			<div className={`relative flex items-start gap-3 ${isBoth ? 'flex-1' : ''}`}>
				<span
					className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
						isBoth ? 'bg-white/15 ring-1 ring-white/25' : 'bg-primary/10 text-primary'
					}`}
				>
					<Search className="h-5 w-5" />
				</span>
				<div className="min-w-0 flex-1">
					<h3 className="text-sm font-bold leading-tight md:text-[15px]">Your products are live in Google!</h3>
					<p className={`mt-1 line-clamp-2 text-xs ${isBoth ? 'text-white/80' : 'text-muted-foreground'}`}>
						Seen by locals in Search, Maps, Images, Shopping and AI (Gemini)
					</p>
				</div>
			</div>
			{isBoth ? (
				<div className="relative mt-3 flex pl-[52px]">
					<Link
						to={shopPath('/results?channel=google')}
						className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
					>
						See early results <ChevronRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			) : null}
		</div>
	);
}
